import { format } from "date-fns";
import { ForwardedRef, forwardRef } from "react";

import {
  Visibility,
  renderOrderedItems,
  useOrderedSections,
  useBranding,
} from "../../hooks";
import {
  FieldItem,
  GroupItem,
  SectionItem,
  printTitle,
  TeamItem,
} from "../../types";
import { getTranslation, getUITranslation } from "../../utils";
import { Page, Section } from "../section-components";
import { TeamLabel } from "../shared-components/team-label";
import {
  SummaryField,
  SummaryGroup,
  SummaryGroupFieldSet,
  SummarySection,
} from "../summary-components";

export type IntakePrintContentProps = {
  formTitle?: string;
  formTranslations?: { language: string; title?: string }[];
  sections: SectionItem[];
  groups: GroupItem[];
  fields: FieldItem[];
  visibility?: Visibility;
  responses: Record<string, any>;
  teamDetails: {
    name: TeamItem["name"];
    picture?: TeamItem["picture"];
  };
  completedOn?: string;
  selectedLanguage?: string;
};

export const IntakePrintContent = forwardRef(
  (
    {
      formTitle,
      formTranslations,
      sections,
      groups,
      fields,
      visibility,
      responses,
      teamDetails,
      completedOn,
      selectedLanguage,
    }: IntakePrintContentProps,
    forwardedRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const branding = useBranding();
    const logoUrl = branding?.logo || teamDetails.picture;
    const shouldDisplayFirmLogo =
      branding?.headerDisplay?.firmLogo !== false && !!logoUrl;

    const translatedFormTitle =
      getTranslation(formTranslations, selectedLanguage)?.title || formTitle;

    const orderedSections = useOrderedSections({
      sections,
      groups,
      fields,
      visibility,
      responses,
    });

    return (
      <div hidden data-testid="intake-submission-print-content">
        <style>
          {`
          @media print {
            @page {
              margin: 20px 0px;
            }
          }
        `}
        </style>
        <div className="print:m-4 print:block" ref={forwardedRef}>
          <Page>
            <div className="ml-[2.2rem]">
              <TeamLabel
                teamName={teamDetails.name}
                teamPicture={logoUrl}
                displayTeamName={false}
                displayTeamPicture={shouldDisplayFirmLogo}
              />
            </div>
            <div className="text-h1 font-poppins font-semibold ml-[3.2rem] mt-[4rem]">
              {translatedFormTitle || printTitle}
            </div>
            <Section>
              {orderedSections.map(({ orderedItems, section }) => {
                const sectionTranslation = getTranslation(
                  section.translations,
                  selectedLanguage,
                );
                const translatedSection = {
                  ...section,
                  title: sectionTranslation?.title || section.title,
                  description:
                    sectionTranslation?.description || section.description,
                };

                return (
                  <SummarySection
                    key={section.uri}
                    section={translatedSection}
                    selectedLanguage={selectedLanguage}
                  >
                    {renderOrderedItems({
                      orderedItems,
                      responses,
                      renderGroup: ({ group, ...rest }) => {
                        const groupTranslation = getTranslation(
                          group.translations,
                          selectedLanguage,
                        );
                        return (
                          <SummaryGroup
                            {...rest}
                            group={{
                              ...group,
                              label: groupTranslation?.label || group.label,
                              description:
                                groupTranslation?.description ||
                                group.description,
                            }}
                          />
                        );
                      },
                      renderField: ({ field, ...rest }) => {
                        const fieldTranslation = getTranslation(
                          field.translations,
                          selectedLanguage,
                        );
                        return (
                          <SummaryField
                            {...rest}
                            field={{
                              ...field,
                              label: fieldTranslation?.label || field.label,
                              description:
                                fieldTranslation?.description ||
                                field.description,
                              options:
                                fieldTranslation?.options || field.options,
                            }}
                            selectedLanguage={selectedLanguage}
                          />
                        );
                      },
                      renderGroupFieldSet: (props) => (
                        <SummaryGroupFieldSet {...props} />
                      ),
                    })}
                  </SummarySection>
                );
              })}
              {completedOn && (
                <div className="text-ui">
                  {`${getUITranslation("formCompletedOn", selectedLanguage)} ${format(new Date(completedOn), "dd MMM yyyy")}`}
                </div>
              )}
            </Section>
          </Page>
        </div>
      </div>
    );
  },
);
